<!-- savedaddress.vue -->
<template>
  <!-- Add New Address -->
  <p @click="addNewAddress" class="text-md font-medium text-primary flex items-center cursor-pointer">
    <UIcon name="i-heroicons-plus-circle-solid" class="w-6 h-6 mr-2" />
    Add new address
  </p>


  <UNotification v-if="lengthError" id="1" :ui="{
    background: 'bg-gradient-to-b from-yellow-50 to-rose-100',
    description: 'text-gray-900 text-xs',
    shadow: 'shadow-none',
    rounded: 'rounded-xl',
    padding: 'p-4',
  }" :close-button="{ icon: '' }" :timeout="0" class="mb-1 w-full">
    <template #description>
      <div class="flex items-center gap-x-3">
        <UIcon name="i-heroicons-exclamation-triangle" class="size-8 text-red-700" />
        <p>
          A maximum of 5 addresses can be added. To add a new one, you must
          delete an existing address.
        </p>
      </div>
    </template>
  </UNotification>

  <UDivider label="OR" />

  <!-- Saved Addresses Section -->
  <div class="flex flex-col items-center w-full overflow-hidden">
    <h2 class="text-[18px] font-semibold text-gray-900 mb-3 self-start">
      Saved Addresses
    </h2>

    <!-- No Saved Addresses -->
    <div v-if="addressStore.addresses.length === 0 && !addressStore.loading"
      class="flex flex-col items-center justify-center text-center mt-4">
      <img src="/v2/images/Addressicon.png" alt="Address Icon" title="Addresses" class="w-[103px] h-[80px]" />

      <p class="text-gray-700 text-sm font-medium">
        You have no saved addresses
      </p>

      <!-- Recent Locations -->
      <div v-if="recentLocations.length > 0" class="flex flex-col gap-2 mt-6 w-full pr-2">
        <p class="text-[20px] md:font-medium font-bold text-left">Recent Locations</p>
        <div class="rounded-md p-1 cursor-pointer flex justify-between items-center hover:bg-gray-50 transition"
          v-for="(location, index) in recentLocations.slice(0, 3)" :key="location.id || index"
          @click="setRecentLocation(location)">
          <div class="flex items-start gap-2 w-[80%]">
            <UIcon name="i-heroicons-clock" class="h-5 w-5 md:h-6 md:w-6 mt-1 text-primary flex shrink-0" />
            <p class="text-gray-700 text-left text-sm">
              {{ location.formatted_address }}
            </p>
          </div>
          <UIcon name="i-heroicons-arrow-up-right" class="h-5 w-5 md:h-6 md:w-6 text-primary" />
        </div>
      </div>
    </div>

    <!-- Saved Address List -->
    <div v-else class="w-full px-0 flex-1">
      <div class="w-full">
        <ul>
          <li v-for="(address, index) in visibleAddresses" :key="address.id"
            class="flex flex-col w-full gap-[12px] bg-white border-[1.5px] rounded-[12px] p-[16px] mb-4 cursor-pointer transition hover:border-primary-500 hover:bg-primary-50"
            :class="[
              isSelected(address)
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200'
            ]" @click="setUserLocation(address)">
            <!-- Address Content -->
            <label class="block">
              <!-- Top Row -->
              <div class="flex items-center justify-between w-full">
                <!-- Left side: Name + Badge -->
                <div class="flex items-center">
                  <p class="text-md font-semibold">
                    {{ address.contact.name }}
                  </p>
                  <UBadge :label="address.label.toUpperCase()"
                    class="ml-2 inline-flex items-center justify-center px-2 py-0.5 gap-1 min-w-[32px] h-4 bg-primary-50 border border-primary-400 text-primary-400 rounded-md text-[10px] font-rubik font-medium leading-[12px] whitespace-nowrap"
                    size="md" v-if="address.label" />
                </div>

                <!-- Right side: Edit / Delete Icons -->
                <div class="flex items-center gap-x-3">
                  <UIcon name="i-lucide-pencil-line"
                    class="size-5 text-primary-500 cursor-pointer hover:text-primary-600 transition"
                    @click.stop="editAddress(address)" />
                  <div class="w-0.5 h-4 bg-gray-200"></div>
                  <UIcon v-if="!addressLoading[address.id]" name="i-heroicons-trash"
                    class="size-6 text-primary-500 cursor-pointer hover:text-red-500 transition"
                    @click.stop="openDeletePopup(address.id)" />
                  <UIcon v-else name="i-heroicons-arrow-path" class="size-5 text-gray-400 animate-spin" />
                </div>
              </div>

              <!-- Address Info -->
              <p class="text-sm mt-1">
                {{ address.door }} {{ address.building }}
                {{ address.locality }} {{ address.city }} -
                {{ address.area_code }}, {{ address.state }}
              </p>
              <p class="text-sm">Phone: {{ address.contact.phone }}</p>
              <p class="text-sm">Email: {{ address.contact.email }}</p>
            </label>
          </li>
        </ul>

        <!-- View More / View Less Button -->
        <div v-if="addressStore.addresses.length > 3" class="text-left mt-2">
          <button v-if="!showAll" class="text-primary font-medium underline" @click="showAll = true">
            View More
          </button>

          <button v-else class="text-primary font-medium underline" @click="showAll = false">
            View Less
          </button>
        </div>

      </div>
    </div>

    <!-- Delete Confirmation Popup -->
    <V2Overlay v-model="isDeleteOpen" container-class="px-4">
      <DeletepopupComponent @confirm="confirmDelete" @close="isDeleteOpen = false" />
    </V2Overlay>
  </div>
</template>



<script setup lang="ts">
import { _outlineOffset } from '#tailwind-config/theme';
import DeletepopupComponent from '~/components/v2/DeletepopupComponent.vue';
import { GoogleAnalytics } from '~/google_analytics/events';
import { EventParameter } from '~/google_analytics/events.enum';
import { getRecentAddresses } from '~/utility/location/recent.location.storage';
import type { Address } from '~/types/address';

const emit = defineEmits(["close"]);

const geolocation = useGeoLocation();
const gps = computed(() => geolocation.selectedAddress?.gps);
const hasLocation = computed(() => !!(gps.value?.latitude && gps.value?.longitude));
const latitude = computed(() => gps?.value?.latitude || 0);
const longitude = computed(() => gps?.value?.longitude || 0);

const router = useRouter();
const route = useRoute();
const location = useLocation();
const recentLocations = ref<Address[]>(getRecentAddresses());
const { validateDeliveryAddress } = useValidateDeliveryAddress();
const { closeLocationUpdate } = useLocationPicker()
const addressStore = useUserAddressStore();
const addressText = computed(() => geolocation.formatSelectedAddress);
const selectedAddress = computed(() => geolocation.selectedAddress);
const isSelected = (addr: Address) => {
  return (
    selectedAddress.value?.id === addr?.id
  );
};
const lengthError = ref(false);
const addressLoading = ref<{ [key: string]: boolean }>({});
const navigated = ref(false);
const checkout = useCheckout();
const fromcheckout = computed(() => route.query?.from === 'checkout')
const fromorders = computed(() => route.query?.from === 'orders')
const isDeleteOpen = ref(false)
const addressToDelete = ref(null)
const errorMessage = ref<HTMLElement | null>(null)
const { $customToast } = useNuxtApp()
const toast = $customToast as any
const showAll = ref(false)

const visibleAddresses = computed(() => {
  if (showAll.value) return addressStore.addresses
  return addressStore.addresses.slice(0, 3)
})

const editAddress = (addressObj: any) => {
  GoogleAnalytics.address({
    [EventParameter.ACTION]: 'edit_address',
    [EventParameter.STATUS]: 'edit_address_initiated',
    [EventParameter.ERROR_MESSAGE]: 'no error'
  });
  sessionStorage.setItem("selectedEditAddress", JSON.stringify(addressObj));
  sessionStorage.setItem("lastPageBeforeMap", route.fullPath);
  navigateTo({ path: '/map', query: { from: 'editAddress' }, replace: true });
};

const navigate = async () => {
  if (navigated.value) return;
  navigated.value = true;
  setTimeout(() => {
    if (geolocation.isValidLocation) {
      if (fromorders.value) {
        router.replace('/cart');
      } else {
        const prev = sessionStorage.getItem("lastPageBeforeMap");
        if (prev) {
          sessionStorage.removeItem("lastPageBeforeMap");
          router.replace(prev);
        } else {
          closeLocationUpdate();
        }
      }
    } else {
      navigated.value = false;
    }
  }, 1000);
};

const setUserLocation = async (address: any) => {
  try {
    address.selectLocation = true;
    GoogleAnalytics.address({
      [EventParameter.ACTION]: 'select_saved_address',
      [EventParameter.STATUS]: 'selected',
      [EventParameter.ERROR_MESSAGE]: 'none'
    });
    await geolocation.setAddOrEditAddress(address, false, true);
    navigate();
  } catch (error) {
    console.error("Error in setUserLocation:", error);
  }
}

const deleteExitUserAddress = async (addressId: string) => {
  try {
    addressLoading.value[addressId] = true;
    await addressStore.deleteAddress(addressId);
    lengthError.value = false;
    GoogleAnalytics.address({
      [EventParameter.ACTION]: 'delete address',
      [EventParameter.STATUS]: 'delete_address_success',
      [EventParameter.ERROR_MESSAGE]: 'none'
    });
  } catch (error) {
    console.error("Error in deleteExitUserAddress:", error);
    GoogleAnalytics.address({
      [EventParameter.ACTION]: 'delete address',
      [EventParameter.STATUS]: 'delete_address_failed',
      [EventParameter.ERROR_MESSAGE]: error || 'delete failed'
    });
  } finally {
    addressLoading.value[addressId] = false;
  }
}


onBeforeMount(async () => {
  await addressStore.fetchAddresses();
});

const setRecentLocation = async (location: Address) => {
  GoogleAnalytics.locationEvent({
    [EventParameter.ACTION]: 'location_selected_from_recent',
    [EventParameter.LATITUDE]: latitude.value,
    [EventParameter.LONGITUDE]: longitude.value,
    [EventParameter.ADDRESS]: addressText.value || 'none',
    [EventParameter.REASON]: 'unknown',
    [EventParameter.LOCATION_AVAILABLE]: hasLocation.value ? "yes" : "no",
    [EventParameter.PROMPT_TYPE]: "unknown",
    [EventParameter.NEEDS_LOCATION_FETCH]: hasLocation.value ? "no" : "yes",
  });
  await geolocation.setSelectedAddress(location);
  navigate();
};

const openDeletePopup = (addressId: any) => {
  GoogleAnalytics.address({
    [EventParameter.ACTION]: 'delete address',
    [EventParameter.STATUS]: 'delete_address_initiated',
    [EventParameter.ERROR_MESSAGE]: 'no error'
  });
  addressToDelete.value = addressId
  isDeleteOpen.value = true
}

const confirmDelete = async () => {
  if (addressToDelete.value) {
    await deleteExitUserAddress(addressToDelete.value)
    isDeleteOpen.value = false
    addressToDelete.value = null
  }
}

const addNewAddress = () => {
  if (addressStore.addresses.length >= 5) {
    GoogleAnalytics.address({
      [EventParameter.ACTION]: 'add_new_address',
      [EventParameter.STATUS]: 'failed',
      [EventParameter.ERROR_MESSAGE]: 'address limit reached'
    });
    lengthError.value = true;
    nextTick(() => {
      errorMessage.value?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    return;
  } else {
    lengthError.value = false;
  }

  GoogleAnalytics.address({
    [EventParameter.ACTION]: 'add new address',
    [EventParameter.STATUS]: 'newaddress_initiated',
    [EventParameter.ERROR_MESSAGE]: 'no error'
  });

  if (fromcheckout.value) {
    sessionStorage.setItem("lastPageBeforeMap", route.fullPath);
    navigateTo({ path: '/map', query: { from: 'checkout' }, replace: true });
  } else {
    sessionStorage.setItem("lastPageBeforeMap", route.fullPath);
    navigateTo({ path: '/map', replace: true });
  }
  emit('close');
};

useSeoMeta({
  title: "Profile Addresses",
});
</script>
